import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowRight, ArrowLeft, Check, Sparkles, QrCode,
  Volume2, Webhook, Timer, Loader2, SkipForward, PartyPopper, Globe,
} from 'lucide-react';
import { FormPicker } from '../components/FormPicker';
import { getTranslations, detectLanguage } from '../utils/i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VOICES = [
  { id: 'nova', label: 'Nova', desc: 'Warm & friendly' },
  { id: 'alloy', label: 'Alloy', desc: 'Balanced & clear' },
  { id: 'echo', label: 'Echo', desc: 'Deep & confident' },
  { id: 'fable', label: 'Fable', desc: 'Expressive & soft' },
  { id: 'onyx', label: 'Onyx', desc: 'Rich & authoritative' },
  { id: 'shimmer', label: 'Shimmer', desc: 'Bright & energetic' },
];

const STEP_ICONS = [Sparkles, QrCode, Volume2, Webhook, Timer, PartyPopper];

const OnboardingWizard = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lang, setLang] = useState(detectLanguage);
  const t = getTranslations(lang);

  const [title, setTitle] = useState('');
  const [formUrl, setFormUrl] = useState('');
  const [createdQR, setCreatedQR] = useState(null);
  const [voice, setVoice] = useState('nova');
  const [welcomeMsg, setWelcomeMsg] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [autoRetryEnabled, setAutoRetryEnabled] = useState(true);

  const next = () => setStep((s) => Math.min(s + 1, 5));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const handleCreateQR = async () => {
    if (!title.trim() || !formUrl.trim()) { toast.error(t.createQR.required); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/qr-codes`, { title, form_url: formUrl }, { headers: { Authorization: `Bearer ${token}` } });
      setCreatedQR(res.data);
      toast.success(t.createQR.success);
      next();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleCustomize = async () => {
    if (!createdQR) { next(); return; }
    setLoading(true);
    try {
      await axios.patch(`${API}/qr-codes/${createdQR.id}/voice`, { voice_preference: voice, welcome_message: welcomeMsg || null }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t.customize.success);
      next();
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const handleWebhook = async () => {
    if (!createdQR || !webhookUrl.trim()) { next(); return; }
    setLoading(true);
    try {
      await axios.patch(`${API}/qr-codes/${createdQR.id}/webhook`, { webhook_url: webhookUrl }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t.webhook.success);
      next();
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const handleAutoRetry = async () => {
    if (!createdQR) { next(); return; }
    setLoading(true);
    try {
      await axios.patch(`${API}/qr-codes/${createdQR.id}/auto-retry`, { enabled: autoRetryEnabled }, { headers: { Authorization: `Bearer ${token}` } });
      next();
    } catch { next(); }
    finally { setLoading(false); }
  };

  const progress = ((step) / 5) * 100;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="onboarding-wizard">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-[#E2E8F0] z-50">
        <div className="h-full bg-[#1d4ed8] transition-all duration-500 ease-out" style={{ width: `${progress}%` }} data-testid="onboarding-progress" />
      </div>

      {/* Language switcher + Step indicators */}
      <div className="max-w-3xl mx-auto pt-8 px-4">
        <div className="flex justify-end mb-4">
          <button
            data-testid="lang-switch"
            onClick={() => setLang(lang === 'en' ? 'el' : 'en')}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#E2E8F0] hover:bg-[#F1F5F9] transition-colors"
          >
            <Globe className="h-3.5 w-3.5" />
            {lang === 'en' ? 'Ελληνικά' : 'English'}
          </button>
        </div>
        <div className="flex items-center justify-between mb-8">
          {t.steps.map((label, i) => {
            const Icon = STEP_ICONS[i];
            const isActive = i === step;
            const isDone = i < step;
            return (
              <div key={`item-${i}`} className="flex flex-col items-center gap-1">
                <div
                  className={`h-10 w-10 rounded-full flex items-center justify-center transition-all ${isDone ? 'bg-green-500 text-white' : isActive ? 'bg-[#1d4ed8] text-white scale-110' : 'bg-[#E2E8F0] text-[#475569]'}`}
                  data-testid={`step-indicator-${i}`}
                >
                  {isDone ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                </div>
                <span className={`text-xs ${isActive ? 'text-[#1d4ed8] font-bold' : 'text-[#475569]'}`}>{label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="max-w-2xl mx-auto px-4 pb-16">
        {/* Step 0: Welcome */}
        {step === 0 && (
          <Card className="p-8 border border-[#E2E8F0] text-center" data-testid="step-welcome">
            <Sparkles className="h-12 w-12 text-[#1d4ed8] mx-auto mb-4" />
            <h1 className="text-3xl font-black mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t.welcome.title}{user?.name ? `, ${user.name}` : ''}!
            </h1>
            <p className="text-[#475569] mb-6 max-w-md mx-auto">{t.welcome.subtitle}</p>
            <Button data-testid="start-onboarding-btn" onClick={next} className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af] px-8" size="lg">
              {t.welcome.cta} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Card>
        )}

        {/* Step 1: Create QR */}
        {step === 1 && (
          <Card className="p-8 border border-[#E2E8F0]" data-testid="step-create-qr">
            <div className="flex items-center gap-3 mb-6">
              <QrCode className="h-8 w-8 text-[#1d4ed8]" />
              <h2 className="text-2xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>{t.createQR.title}</h2>
            </div>
            <p className="text-[#475569] mb-6">{t.createQR.subtitle}</p>

            <div className="mb-6">
              <FormPicker onSelectForm={(form) => { setTitle(form.title); setFormUrl(form.url); }} />
            </div>

            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#E2E8F0]" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-[#475569]">{t.createQR.orManual}</span></div>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="onb-title">{t.createQR.titleLabel} *</Label>
                <Input id="onb-title" data-testid="onb-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t.createQR.titlePlaceholder} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="onb-url">{t.createQR.urlLabel} *</Label>
                <Input id="onb-url" data-testid="onb-url-input" type="url" value={formUrl} onChange={(e) => setFormUrl(e.target.value)} placeholder={t.createQR.urlPlaceholder} className="mt-1" />
                <p className="text-xs text-[#475569] mt-1">{t.createQR.urlHelp}</p>
              </div>
            </div>
            <div className="flex justify-between mt-8">
              <Button variant="outline" onClick={prev} className="rounded-full"><ArrowLeft className="h-4 w-4 mr-2" /> {t.back}</Button>
              <Button data-testid="create-qr-onb-btn" onClick={handleCreateQR} disabled={loading || !title.trim() || !formUrl.trim()} className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]">
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <QrCode className="h-4 w-4 mr-2" />}
                {t.createQR.cta}
              </Button>
            </div>
          </Card>
        )}

        {/* Step 2: Customize */}
        {step === 2 && (
          <Card className="p-8 border border-[#E2E8F0]" data-testid="step-customize">
            <div className="flex items-center gap-3 mb-6">
              <Volume2 className="h-8 w-8 text-[#1d4ed8]" />
              <h2 className="text-2xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>{t.customize.title}</h2>
            </div>
            <p className="text-[#475569] mb-6">{t.customize.subtitle}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
              {VOICES.map((v) => (
                <button key={v.id} onClick={() => setVoice(v.id)} data-testid={`voice-option-${v.id}`}
                  className={`p-3 rounded-lg border text-left transition-all ${voice === v.id ? 'border-[#1d4ed8] bg-[#EFF6FF] ring-2 ring-[#1d4ed8]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`}>
                  <p className="font-bold text-sm">{v.label}</p>
                  <p className="text-xs text-[#475569]">{v.desc}</p>
                </button>
              ))}
            </div>
            <div>
              <Label htmlFor="onb-welcome">{t.customize.welcomeLabel}</Label>
              <Input id="onb-welcome" data-testid="onb-welcome-input" value={welcomeMsg} onChange={(e) => setWelcomeMsg(e.target.value)} placeholder={t.customize.welcomePlaceholder} className="mt-1" />
            </div>
            <div className="flex justify-between mt-8">
              <Button variant="outline" onClick={prev} className="rounded-full"><ArrowLeft className="h-4 w-4 mr-2" /> {t.back}</Button>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={next} className="rounded-full text-[#475569]"><SkipForward className="h-4 w-4 mr-1" /> {t.customize.skip}</Button>
                <Button data-testid="save-customize-btn" onClick={handleCustomize} disabled={loading} className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]">
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                  {t.customize.cta}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Step 3: Webhook */}
        {step === 3 && (
          <Card className="p-8 border border-[#E2E8F0]" data-testid="step-webhook">
            <div className="flex items-center gap-3 mb-6">
              <Webhook className="h-8 w-8 text-[#1d4ed8]" />
              <h2 className="text-2xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>{t.webhook.title}</h2>
            </div>
            <p className="text-[#475569] mb-6">{t.webhook.subtitle}</p>
            <div>
              <Label htmlFor="onb-webhook">{t.webhook.urlLabel}</Label>
              <Input id="onb-webhook" data-testid="onb-webhook-input" type="url" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} placeholder={t.webhook.urlPlaceholder} className="mt-1" />
            </div>
            <div className="flex justify-between mt-8">
              <Button variant="outline" onClick={prev} className="rounded-full"><ArrowLeft className="h-4 w-4 mr-2" /> {t.back}</Button>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={next} className="rounded-full text-[#475569]"><SkipForward className="h-4 w-4 mr-1" /> {t.webhook.skip}</Button>
                <Button data-testid="save-webhook-btn" onClick={handleWebhook} disabled={loading} className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]">
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                  {webhookUrl.trim() ? t.webhook.cta : t.webhook.ctaEmpty}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Step 4: Auto-Retry */}
        {step === 4 && (
          <Card className="p-8 border border-[#E2E8F0]" data-testid="step-auto-retry">
            <div className="flex items-center gap-3 mb-6">
              <Timer className="h-8 w-8 text-[#1d4ed8]" />
              <h2 className="text-2xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>{t.autoRetry.title}</h2>
            </div>
            <p className="text-[#475569] mb-6">{t.autoRetry.subtitle}</p>
            <div className="flex gap-4">
              <button data-testid="auto-retry-on" onClick={() => setAutoRetryEnabled(true)}
                className={`flex-1 p-4 rounded-lg border text-center transition-all ${autoRetryEnabled ? 'border-[#1d4ed8] bg-[#EFF6FF] ring-2 ring-[#1d4ed8]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`}>
                <Timer className="h-6 w-6 mx-auto mb-2 text-[#1d4ed8]" />
                <p className="font-bold">{t.autoRetry.enable}</p>
                <p className="text-xs text-[#475569]">{t.autoRetry.enableDesc}</p>
              </button>
              <button data-testid="auto-retry-off" onClick={() => setAutoRetryEnabled(false)}
                className={`flex-1 p-4 rounded-lg border text-center transition-all ${!autoRetryEnabled ? 'border-[#475569] bg-[#F1F5F9] ring-2 ring-[#475569]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`}>
                <SkipForward className="h-6 w-6 mx-auto mb-2 text-[#475569]" />
                <p className="font-bold">{t.autoRetry.disable}</p>
                <p className="text-xs text-[#475569]">{t.autoRetry.disableDesc}</p>
              </button>
            </div>
            <div className="flex justify-between mt-8">
              <Button variant="outline" onClick={prev} className="rounded-full"><ArrowLeft className="h-4 w-4 mr-2" /> {t.back}</Button>
              <Button data-testid="save-auto-retry-btn" onClick={handleAutoRetry} disabled={loading} className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]">
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                {t.autoRetry.cta}
              </Button>
            </div>
          </Card>
        )}

        {/* Step 5: Done */}
        {step === 5 && (
          <Card className="p-8 border border-[#E2E8F0] text-center" data-testid="step-done">
            <PartyPopper className="h-16 w-16 text-[#1d4ed8] mx-auto mb-4" />
            <h1 className="text-3xl font-black mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{t.done.title}</h1>
            <p className="text-[#475569] mb-6 max-w-md mx-auto">{t.done.subtitle}</p>
            {createdQR && (
              <div className="inline-block bg-white border border-[#E2E8F0] rounded-lg p-4 mb-6">
                <img src={`${API}/qr-codes/${createdQR.id}/image?token=${token}`} alt={`QR code for ${createdQR.title}`}
                  className="w-40 h-40 mx-auto" style={{ imageRendering: 'pixelated' }} data-testid="onboarding-qr-image" />
                <p className="text-sm font-bold mt-2">{createdQR.title}</p>
              </div>
            )}
            <div className="flex gap-3 justify-center">
              <Button data-testid="go-to-dashboard-btn" onClick={() => navigate('/dashboard')} className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]" size="lg">
                {t.done.dashboard} <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Button data-testid="go-to-qr-codes-btn" variant="outline" onClick={() => navigate('/qr-codes')} className="rounded-full" size="lg">
                {t.done.qrCodes}
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default OnboardingWizard;
